import type {
  ApiReturnAllSensor,
  ApiReturnDeviceSite,
  ApiReturnDeviceType,
  ApiReturnDeviceTypeMeasureType,
  ApiReturnMeasurePoint,
  ApiReturnMeasureType,
  ApiReturnSite,
  ApiReturnThematic,
} from '@highimpact-frontend/api-types';
import { GridColDef } from '@mui/x-data-grid-pro';
import DeviceSiteService from '../../../api/services/config/deviceSite/DeviceSiteService';
import CreateStation from '../../../components/stations/CreateStation';
import ConfirmModal from '../../../components/utils/confirmModal/ConfirmModal';
import { MapDisplayer } from '../../../components/utils/map/MapDisplayer';
import ListDataGrid from '../../../components/utils/MUISpecifics/data-grid/ListDataGrig';
import PermissionDecoratorV2 from '../../../components/utils/permission/PermissionDecoratorV2';
import { resetExternalState } from '../../../store/data/slice';
import { DataState } from '../../../store/data/state';
import { useAppDispatch } from '../../../store/hooks';
import { getEntityById, getOrElse } from '../../../utils/functions';
import {
  emptyPolygon,
  wktToPolygon,
} from '../../../utils/geom/highImpactGeometry';
import { useModal } from '../../../utils/hooks/useModal';
import { useSubmit } from '../../../utils/hooks/useSubmit';
import { StationActionsCell } from './StationActionsCell';
import { useTranslation } from 'react-i18next';

interface StationListProps {
  freeDevices: ApiReturnAllSensor[];
  allDevices: ApiReturnAllSensor[];
  thematics: ApiReturnThematic[];
  sites: DataState<string, { [KEY in number]: ApiReturnSite }>;
  device_sites: ApiReturnDeviceSite[];
  measureTypes: DataState<string, { [KEY in number]: ApiReturnMeasureType }>;
  measurePoints: ApiReturnMeasurePoint[];
  deviceTypes: DataState<string, { [KEY in number]: ApiReturnDeviceType }>;
  deviceTypeMeasureTypes: DataState<
    string,
    { [KEY in number]: ApiReturnDeviceTypeMeasureType }
  >;
  siteId: number;
}

const StationList: React.FC<StationListProps> = ({
  freeDevices,
  allDevices: devices,
  measureTypes,
  deviceTypes,
  deviceTypeMeasureTypes,
  measurePoints,
  siteId,
  sites,
}) => {
  const t = useTranslation('component.stations').t;
  const dispatch = useAppDispatch();

  const createModal = useModal();

  const deleteModal = useModal<{
    callback: () => void;
    message: string;
    target: string;
    manualSensorId: number;
  }>();

  const { submit } = useSubmit();

  const deleteStation = async (id: number) => {
    submit({
      callbacks: [
        async () =>
          await DeviceSiteService.unassign(id, new Date().toISOString()),
        () => dispatch(resetExternalState({ external_key: 'devices' })),
        () => dispatch(resetExternalState({ external_key: 'site_devices' })),
        () => dispatch(resetExternalState({ external_key: 'measure_points' })),
        () => dispatch(resetExternalState({ external_key: 'sites' })),
      ],
      errorMessages: {
        anyError: t(
          'Une erreur est survenue lors de la suppression de la station'
        ),
        unicityError: t(
          'Impossible de supprimer cette station (erreur de contrainte)'
        ),
      },
      successMessage: t('La station a été supprimée avec succès'),
    });
  };

  const createRow = (station: ApiReturnMeasurePoint) => {
    return {
      id: getOrElse(station.id),
      name: getOrElse(station.name),
      type: getOrElse(
        getEntityById(
          getEntityById(station.device_site?.device_id!, devices)
            ?.device_type_id!,
          Object.values(deviceTypes.data)
        )?.name
      ),
      Actions: String(station.id),
      station,
      onDelete: () => {
        deleteModal.open({
          callback: () => deleteStation(station.device_site?.id!),
          message: t('Êtes-vous sûr de vouloir supprimer la station'),
          target: station.name,
          manualSensorId: station.id,
        });
      },
      /**
       * If we want to pass more than 1 handler for another buttons, we can do it like
       * a props:
       *
       * onLog: () => {console.log("Button log was clicked!", station.id);},
       *
       * And than use them in target component (StationActionsCell.tsx)
       */
    };
  };

  const rows = measurePoints
    .filter((ds) => ds.site_id === siteId)
    .map(createRow);

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      field: 'name',
      headerName: t('Nom'),
      sortable: true,
      filterable: true,
      minWidth: 150,
      flex: 1,
      headerClassName: 'list-data-grid-style_table-header',
    },
    {
      field: 'type',
      headerName: t('Type de dispositif'),
      sortable: true,
      filterable: true,
      minWidth: 150,
      flex: 1,
      headerClassName: 'list-data-grid-style_table-header',
    },
    {
      field: 'actions',
      headerName: t('Actions'),
      minWidth: 100,
      headerClassName: 'list-data-grid-style_table-header',
      filterable: false,
      sortable: false,
      renderCell: (params) => <StationActionsCell params={params} />,
    },
  ];

  return (
    <div className={`list-container`}>
      <div className="toolbar">
        <PermissionDecoratorV2 item="device_sites_create_button">
          <button
            type="submit"
            className="uk-button uk-button-default"
            onClick={() => createModal.open()}
          >
            {t('Créer une station de mesure')}
          </button>
        </PermissionDecoratorV2>
      </div>
      <PermissionDecoratorV2 item={'device_sites_map'}>
        <div
          style={{
            margin: '20px 0',
            maxWidth: '100%',
            boxSizing: 'border-box',
          }}
        >
          <MapDisplayer
            data={measurePoints
              .filter((ds) => ds.site_id === siteId)
              .map(
                (sr) =>
                  (sr.geom_point
                    ? typeof sr.geom_point === 'string'
                      ? wktToPolygon(sr.geom_point)
                      : Array.isArray(sr.geom_point)
                      ? null
                      : sr.geom_point
                    : null) ?? emptyPolygon()
              )}
          />
        </div>
      </PermissionDecoratorV2>

      <ListDataGrid
        rows={rows}
        columns={columns}
        checkboxSelection={false}
        //if in feture we want to do list scrollable with limitet height
        //height='calc(100vh - 80px - 125px)'
        slotProps={{
          columnMenu: {
            slots: {
              columnMenuSortItem: null,
              columnMenuColumnsItem: null,
              columnMenuPinningItem: null,
            },
          },
        }}
      />
      {deleteModal.isOpen && (
        <ConfirmModal
          onCancel={deleteModal.close}
          onConfirm={() => deleteModal.params?.callback?.()}
          open={deleteModal.isOpen}
        >
          <p>
            {deleteModal.params?.message!}{' '}
            <span className="uk-text-bold">{deleteModal.params?.target}</span> ?
          </p>
        </ConfirmModal>
      )}

      {/* {editModal.isOpen && (
        <UpdateManualDevice
          onCancel={editModal.close}
          isOpen={editModal.isOpen}
          projects={projects}
          project_sites={project_sites}
          sites={Object.values(sites.data)}
          thematics={thematics}
          closeModal={editModal.close}
          device={editModal.params?.manualSensor!}
          measureTypes={measureTypes}
          deviceMeasureTypes={device_measureTypes}
        />
      )} */}
      {createModal.isOpen && (
        <CreateStation
          onCancel={createModal.close}
          isOpen={createModal.isOpen}
          device_types={Object.values(deviceTypes.data)}
          deviceTypeMeasureTypes={deviceTypeMeasureTypes}
          measureTypes={measureTypes}
          allDevices={devices}
          freeDevices={freeDevices}
          site={Object.values(sites.data).find((s) => s.id === siteId)!}
        />
      )}
    </div>
  );
};

export default StationList;
